import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ruler, ChevronDown, ChevronRight } from "lucide-react";

export default function SizesPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group dialog
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", sort_order: 0, is_active: true });

  // Size dialog
  const [showSizeDialog, setShowSizeDialog] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [sizeGroupId, setSizeGroupId] = useState("");
  const [sizeForm, setSizeForm] = useState({ name: "", short_name: "", sort_order: 0, is_active: true });

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get("/size-groups");
      setGroups(res.data);
    } catch { toast.error("Failed to load size groups"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const toggleGroup = (id) => setExpandedGroups((p) => ({ ...p, [id]: !p[id] }));

  // Group CRUD
  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", sort_order: groups.length + 1, is_active: true });
    setShowGroupDialog(true);
  };
  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, sort_order: g.sort_order || 0, is_active: g.is_active !== false });
    setShowGroupDialog(true);
  };
  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = { ...groupForm, sort_order: parseInt(groupForm.sort_order) || 0 };
      if (editingGroup) { await api.put(`/size-groups/${editingGroup.size_group_id}`, payload); toast.success("Updated"); }
      else { await api.post("/size-groups", payload); toast.success("Created"); }
      setShowGroupDialog(false); fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this size group and all its sizes? Products using it will be unassigned.")) return;
    try { await api.delete(`/size-groups/${id}`); toast.success("Deleted"); fetchGroups(); }
    catch { toast.error("Failed"); }
  };

  // Size CRUD
  const openAddSize = (groupId) => {
    setEditingSize(null); setSizeGroupId(groupId);
    const g = groups.find((x) => x.size_group_id === groupId);
    setSizeForm({ name: "", short_name: "", sort_order: (g?.sizes?.length || 0) + 1, is_active: true });
    setShowSizeDialog(true);
  };
  const openEditSize = (size) => {
    setEditingSize(size); setSizeGroupId(size.size_group_id);
    setSizeForm({ name: size.name, short_name: size.short_name || "", sort_order: size.sort_order || 0, is_active: size.is_active !== false });
    setShowSizeDialog(true);
  };
  const saveSize = async () => {
    if (!sizeForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = { ...sizeForm, size_group_id: sizeGroupId, sort_order: parseInt(sizeForm.sort_order) || 0 };
      if (editingSize) { await api.put(`/sizes/${editingSize.size_id}`, payload); toast.success("Updated"); }
      else { await api.post("/sizes", payload); toast.success("Created"); }
      setShowSizeDialog(false); fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteSize = async (id) => {
    if (!window.confirm("Delete this size?")) return;
    try { await api.delete(`/sizes/${id}`); toast.success("Deleted"); fetchGroups(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="sizes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="sizes-title">Size Definitions</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create size groups (e.g., Pizza Sizes, Sub Sizes) then define sizes within each.
            Products pick a group — modifiers adjust pricing per size.
          </p>
        </div>
        <Button onClick={openAddGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-size-group-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Size Group
        </Button>
      </div>

      <div className="space-y-4" data-testid="size-groups-list">
        {groups.map((group) => {
          const isExpanded = expandedGroups[group.size_group_id];
          return (
            <div key={group.size_group_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`size-group-${group.size_group_id}`}>
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => toggleGroup(group.size_group_id)}>
                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <Ruler className="h-4 w-4 text-[var(--brand-primary)]" />
                <div className="flex-1">
                  <span className="font-semibold text-sm text-slate-800">{group.name}</span>
                  <span className="text-xs text-slate-400 ml-2">{group.sizes?.length || 0} sizes</span>
                  {!group.is_active && <Badge variant="secondary" className="text-xs ml-2">Inactive</Badge>}
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditGroup(group)} data-testid={`edit-sg-${group.size_group_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteGroup(group.size_group_id)} data-testid={`del-sg-${group.size_group_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-slate-100">
                  <div className="grid grid-cols-[minmax(0,1fr)_80px_60px_60px_80px] gap-2 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-white border-b border-slate-100">
                    <div>Size Name</div>
                    <div className="text-center">Short</div>
                    <div className="text-center">Order</div>
                    <div className="text-center">Active</div>
                    <div className="text-right">Actions</div>
                  </div>
                  {group.sizes?.map((size) => (
                    <div key={size.size_id} className="grid grid-cols-[minmax(0,1fr)_80px_60px_60px_80px] gap-2 items-center px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 group" data-testid={`size-row-${size.size_id}`}>
                      <div className="flex items-center gap-2">
                        <Ruler className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">{size.name}</span>
                      </div>
                      <div className="text-center"><Badge variant="outline" className="text-xs font-mono">{size.short_name || "-"}</Badge></div>
                      <div className="text-center text-sm text-slate-500">{size.sort_order}</div>
                      <div className="text-center">
                        <Badge variant={size.is_active ? "default" : "secondary"} className="text-xs">{size.is_active ? "On" : "Off"}</Badge>
                      </div>
                      <div className="text-right flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditSize(size)} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-size-${size.size_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
                        <button onClick={() => deleteSize(size.size_id)} className="p-1 rounded hover:bg-red-100" data-testid={`del-size-${size.size_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                  <div className="px-4 py-2">
                    <button onClick={() => openAddSize(group.size_group_id)} className="text-xs text-[var(--brand-primary)] hover:underline font-medium" data-testid={`add-size-in-${group.size_group_id}`}>+ Add Size</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="text-center py-12 text-slate-400 bg-white border border-slate-200 rounded-lg">
            No size groups yet. Create your first group (e.g., "Pizza Sizes").
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">How It Works</h3>
        <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
          <li><strong>Size Groups</strong> define types of sizing (Pizza Sizes, Sub Sizes, Drink Sizes)</li>
          <li>Each group has its own <strong>sizes</strong> (Small, Large, Wrap, Sicilian, etc.)</li>
          <li>On each product, pick which <strong>Size Group</strong> applies (or none for unsized items)</li>
          <li>Set a <strong>base price per size</strong> on the product</li>
          <li>Set <strong>modifier price offsets per size</strong> on each modifier</li>
        </ul>
      </div>

      {/* Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent data-testid="size-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingGroup ? "Edit Size Group" : "New Size Group"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">A size group contains related sizes (e.g., all pizza sizes)</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Group Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder='e.g., Pizza Sizes, Sub Sizes' data-testid="sg-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={groupForm.sort_order} onChange={(e) => setGroupForm({ ...groupForm, sort_order: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={groupForm.is_active} onCheckedChange={(v) => setGroupForm({ ...groupForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={saveGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="sg-save-btn">{editingGroup ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Size Dialog */}
      <Dialog open={showSizeDialog} onOpenChange={setShowSizeDialog}>
        <DialogContent data-testid="size-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingSize ? "Edit Size" : "New Size"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Define a size option within this group</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Size Name</Label>
              <Input value={sizeForm.name} onChange={(e) => setSizeForm({ ...sizeForm, name: e.target.value })} placeholder='e.g., Large 16"' data-testid="size-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Short Name</Label>
                <Input value={sizeForm.short_name} onChange={(e) => setSizeForm({ ...sizeForm, short_name: e.target.value })} placeholder="LG" data-testid="size-short-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={sizeForm.sort_order} onChange={(e) => setSizeForm({ ...sizeForm, sort_order: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={sizeForm.is_active} onCheckedChange={(v) => setSizeForm({ ...sizeForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSizeDialog(false)}>Cancel</Button>
            <Button onClick={saveSize} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="size-save-btn">{editingSize ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
